import React, { useMemo } from 'react';
import { Box, Flex, Text } from '@mantine/core';
import { ActiveFilter, FilterConfig } from '../../types';
import { MAIN_DARK_PRIMARY } from '../../../../../constants/colors';


export interface RightPanelHeaderProps {
  filtersConfig: { idea?: Record<string, FilterConfig>; evidence?: Record<string, FilterConfig> };
  selectedCategory: string;
  selectedChildFilter: string | null;
  localActiveFilters: ActiveFilter[];
  handleRemoveFilter: (filterName: string) => void;
  hasChildActiveFilters: () => boolean;
}

function resolveCurrentFilter(
  filtersConfig: RightPanelHeaderProps['filtersConfig'],
  selectedCategory: string,
  selectedChildFilter: string | null
): { label: string | null; name: string | null } {
  if (!selectedCategory || !filtersConfig) {
    return { label: null, name: null };
  }
  let filterConfig: FilterConfig | undefined = filtersConfig.idea?.[selectedCategory];
  let resolvedName: string = selectedCategory;

  if (filterConfig) {
    if (filterConfig.childFilters && selectedChildFilter && selectedChildFilter !== selectedCategory) {
      filterConfig = filterConfig.childFilters[selectedChildFilter];
      resolvedName = selectedChildFilter;
    } else if (filterConfig.childFilters && !selectedChildFilter) {
      // fetchableCheckbox: don't expand children; show category as current filter
      if (filterConfig.type === 'fetchableCheckbox') {
        resolvedName = selectedCategory;
      } else {
        return {
          label: filterConfig?.label ?? selectedCategory,
          name: null,
        };
      }
    } else {
      resolvedName = selectedCategory;
    }
  } else {
    const categoryData = filtersConfig.evidence?.[selectedCategory];
    if (categoryData?.childFilters && selectedChildFilter && selectedChildFilter !== selectedCategory) {
      filterConfig = categoryData.childFilters[selectedChildFilter];
      resolvedName = selectedChildFilter;
    } else if (categoryData?.childFilters && !selectedChildFilter) {
      if (categoryData.type === 'fetchableCheckbox') {
        filterConfig = categoryData;
        resolvedName = selectedCategory;
      } else {
        return {
          label: categoryData?.label ?? selectedCategory,
          name: null,
        };
      }
    } else {
      filterConfig = categoryData;
      resolvedName = selectedCategory;
    }
  }

  return {
    label: filterConfig?.label ?? null,
    name: resolvedName,
  };
}

const RightPanelHeader: React.FC<RightPanelHeaderProps> = ({
  filtersConfig,
  selectedCategory,
  selectedChildFilter,
  localActiveFilters,
  handleRemoveFilter,
  hasChildActiveFilters,
}) => {
  const { label, name } = useMemo(
    () => resolveCurrentFilter(filtersConfig, selectedCategory, selectedChildFilter),
    [filtersConfig, selectedCategory, selectedChildFilter]
  );

  const hasActiveFilterForCurrent = name
    ? localActiveFilters.some((f) => f.name === name)
    : false;
  const showClear = !!name;

  const isPlaceholder = !label && !name;

  return (
    <Flex
      justify="space-between"
      align="center"
      mb="sm"
      pb="xs"
    >
      <Box >
        <Text
          size="sm"
          fw={600}
          color={MAIN_DARK_PRIMARY}
        >
          {label ?? name ?? 'Select a filter'}
        </Text>
      </Box>
      {showClear && (
        <Box 
          mr={7}
          onClick={() => name && hasActiveFilterForCurrent && handleRemoveFilter(name)}
          title="Clear filters"
          aria-label="Clear filters"
          sx={{ 
            cursor: hasActiveFilterForCurrent ? 'pointer' : 'default',
            '&:hover .clear-text': { 
              color: MAIN_DARK_PRIMARY,
            },
          }}
        >
          <Text className="clear-text" fw={500} size="xs" sx={{ color:  '#6b7280'  }}>
            Clear
          </Text>
        </Box>
      )}
    </Flex>
  );
};

export default RightPanelHeader;
